import { DragEvent, useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Camera, Grid2X2, Plus, Send, Trash2, X } from "lucide-react";
import { useAuth, useMessage, useTheme } from "../../global-components/src";
import { getImagePathBucketPacoca } from "../../global-components/src/utils/utils";
import { PACOCA_API_URL } from "../../global-components/src/utils/vars";
import { api } from "../../services/api";
import { getCachedInstants, saveCachedInstants } from "../../utils/instants-cache";
import { shouldAdvanceInstant } from "../../utils/instant-navigation";
import { Instant } from "../../types";
import "./style.css";

const REACTIONS = ["😂", "❤️", "😢", "🥐", "😮"];

const preventInstantMediaDrag = (event: DragEvent<HTMLImageElement>) => {
  event.preventDefault();
  event.stopPropagation();
};

const protectedInstantImageProps = {
  draggable: false,
  onDragStart: preventInstantMediaDrag,
};

interface EmojiParticle {
  id: number;
  emoji: string;
  left: number;
  delay: number;
  drift: number;
  size: number;
}

interface InstantDockProps {
  collapsed?: boolean;
  reloadWhenEmptyKey?: string;
}

export default function InstantDock({ collapsed = false, reloadWhenEmptyKey }: InstantDockProps) {
  const { token, user } = useAuth();
  const { setMessage } = useMessage();
  const { isDarkMode } = useTheme();
  const [instants, setInstants] = useState<Instant[]>([]);
  const [loadingInstants, setLoadingInstants] = useState(true);
  const [opened, setOpened] = useState<Instant | null>(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [uploading, setUploading] = useState(false);
  const [reply, setReply] = useState("");
  const [emojiParticles, setEmojiParticles] = useState<EmojiParticle[]>([]);
  const [showLibrary, setShowLibrary] = useState(false);
  const [myInstants, setMyInstants] = useState<Instant[]>([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(-1);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const instantsRef = useRef<Instant[]>([]);
  const nextCursorRef = useRef<string | null>(null);
  const hasMoreRef = useRef(true);
  const loadingPagePromiseRef = useRef<Promise<Instant[]> | null>(null);
  const cacheReadyUserIdRef = useRef<number | null>(null);
  const pointerStartRef = useRef<{ x: number; y: number } | null>(null);
  const advancingRef = useRef(false);

  const persistQueue = useCallback((items: Instant[], nextCursor = nextCursorRef.current, hasMore = hasMoreRef.current) => {
    instantsRef.current = items;
    setInstants(items);
    saveCachedInstants(user?.id, { items, nextCursor, hasMore });
  }, [user?.id]);

  const load = useCallback((reset = true): Promise<Instant[]> => {
    if (loadingPagePromiseRef.current) return loadingPagePromiseRef.current;
    if (!reset && !hasMoreRef.current) return Promise.resolve(instantsRef.current);

    const task = (async () => {
      if (reset && instantsRef.current.length === 0) setLoadingInstants(true);
      try {
        const response = await api(token).get("/instants", {
          params: { limit: 8, ...(!reset && nextCursorRef.current ? { cursor: nextCursorRef.current } : {}) },
        });
        const page: Instant[] = response.data?.data ?? [];
        const base = reset ? [] : instantsRef.current;
        const seen = new Set(base.map(item => item.id));
        const items = [...base, ...page.filter(item => !seen.has(item.id))];
        nextCursorRef.current = response.data?.next_cursor ?? null;
        hasMoreRef.current = response.data?.has_more === true;
        persistQueue(items, nextCursorRef.current, hasMoreRef.current);
        return items;
      } catch {
        return instantsRef.current;
      } finally {
        setLoadingInstants(false);
      }
    })();

    loadingPagePromiseRef.current = task;
    task.finally(() => {
      if (loadingPagePromiseRef.current === task) loadingPagePromiseRef.current = null;
    });
    return task;
  }, [persistQueue, token]);

  useEffect(() => {
    cacheReadyUserIdRef.current = null;
    const cached = getCachedInstants(user?.id);
    if (cached) {
      instantsRef.current = cached.items;
      nextCursorRef.current = cached.nextCursor;
      hasMoreRef.current = cached.hasMore;
      setInstants(cached.items);
      setLoadingInstants(false);
    }
    cacheReadyUserIdRef.current = user?.id ?? null;
    void load(true);
  }, [load, user?.id]);

  useEffect(() => {
    if (!user?.id || cacheReadyUserIdRef.current !== user.id || instantsRef.current.length > 0) return;
    void load(true);
  }, [load, reloadWhenEmptyKey, user?.id]);
  useEffect(() => () => streamRef.current?.getTracks().forEach(track => track.stop()), []);

  const closeCamera = () => {
    streamRef.current?.getTracks().forEach(track => track.stop());
    streamRef.current = null;
    setCameraOpen(false);
    setCameraError("");
  };

  const openCamera = async () => {
    if (!navigator.mediaDevices?.getUserMedia) {
      setCameraError("A câmera não está disponível neste navegador.");
      setCameraOpen(true);
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" } },
        audio: false,
      });
      streamRef.current = stream;
      setCameraError("");
      setCameraOpen(true);
      requestAnimationFrame(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          void videoRef.current.play();
        }
      });
    } catch {
      setCameraError("Permita o acesso à câmera para publicar um momento.");
      setCameraOpen(true);
    }
  };

  const capture = () => {
    const video = videoRef.current;
    if (!video?.videoWidth || !video.videoHeight || uploading) return;
    const canvas = document.createElement("canvas");
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d")?.drawImage(video, 0, 0, canvas.width, canvas.height);
    canvas.toBlob(async blob => {
      if (!blob) return;
      try {
        setUploading(true);
        const file = new File([blob], `instant-${Date.now()}.jpg`, { type: "image/jpeg" });
        const { data: uploadUrls } = await axios.post(
          `${PACOCA_API_URL}/generate-upload-urls`,
          { files: [{ type: "image", name: file.name }] },
          { headers: { Authorization: `Bearer ${token}` } },
        );
        await axios.put(uploadUrls[0].url, file, { headers: { "Content-Type": file.type } });
        await api(token).post("/instants", { image_path: uploadUrls[0].path });
        closeCamera();
        await load();
        setMessage("Momento publicado.", "success");
      } catch (error: any) {
        setMessage(error?.response?.data?.message ?? "Não foi possível publicar o momento.", "error");
      } finally {
        setUploading(false);
      }
    }, "image/jpeg", .92);
  };

  const closeViewer = () => {
    const remaining = viewerIndex >= 0 ? instantsRef.current.slice(viewerIndex + 1) : instantsRef.current;
    persistQueue(remaining);
    setOpened(null);
    setViewerIndex(-1);
    setShowLibrary(false);
    setReply("");
    if (remaining.length < 3 && hasMoreRef.current) void load(false);
  };

  const openAt = async (requestedIndex: number, source?: Instant[]): Promise<void> => {
    let items = source ?? instantsRef.current;
    if (requestedIndex >= items.length && hasMoreRef.current) {
      items = await load(false);
    }

    const instant = items[requestedIndex];
    if (!instant) {
      persistQueue([]);
      setOpened(null);
      setViewerIndex(-1);
      return;
    }

    try {
      const response = await api(token).post(`/instants/${instant.id}/open`);
      setViewerIndex(requestedIndex);
      setOpened({ ...instant, ...response.data?.instant });
      setReply("");
      if (requestedIndex >= items.length - 3 && hasMoreRef.current) void load(false);
    } catch (error: any) {
      if (error?.response?.status === 410) {
        const filtered = items.filter(item => item.id !== instant.id);
        persistQueue(filtered);
        await openAt(requestedIndex, filtered);
      }
    }
  };

  const openInstant = async (instant: Instant) => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    const index = Math.max(0, instantsRef.current.findIndex(item => item.id === instant.id));
    try { await openAt(index); } finally { advancingRef.current = false; }
  };

  const advanceInstant = () => {
    if (viewerIndex < 0 || advancingRef.current) return;
    advancingRef.current = true;
    void openAt(viewerIndex + 1).finally(() => { advancingRef.current = false; });
  };

  const burstEmoji = (emoji: string) => {
    const seed = Date.now();
    const particles = Array.from({ length: 18 }, (_, index) => ({
      id: seed + index,
      emoji,
      left: 18 + Math.random() * 64,
      delay: Math.random() * .32,
      drift: -70 + Math.random() * 140,
      size: 22 + Math.random() * 18,
    }));
    setEmojiParticles(current => [...current, ...particles]);
    window.setTimeout(() => {
      const ids = new Set(particles.map(particle => particle.id));
      setEmojiParticles(current => current.filter(particle => !ids.has(particle.id)));
    }, 2100);
  };

  const openLibrary = async () => {
    setShowLibrary(true);
    setLoadingLibrary(true);
    try {
      const response = await api(token).get("/instants/mine");
      setMyInstants(response.data?.data ?? []);
    } catch {
      setMyInstants([]);
    } finally {
      setLoadingLibrary(false);
    }
  };

  const deleteInstant = async (instantId: number) => {
    try {
      await api(token).delete(`/instants/${instantId}`);
      setMyInstants(current => current.filter(instant => instant.id !== instantId));
      setMessage("Momento apagado.", "success");
    } catch {
      setMessage("Não foi possível apagar o momento.", "error");
    }
  };

  const react = async (emoji: string) => {
    if (!opened) return;
    burstEmoji(emoji);
    setOpened({ ...opened, user_reaction: emoji });
    try { await api(token).post(`/instants/${opened.id}/react`, { emoji }); } catch { /* optimistic */ }
  };

  const sendReply = async () => {
    if (!opened || !reply.trim()) return;
    try {
      await api(token).post(`/instants/${opened.id}/reply`, { text: reply.trim() });
      setReply("");
      setMessage("Resposta enviada.", "success");
    } catch {
      setMessage("Não foi possível enviar a resposta.", "error");
    }
  };

  const current = instants[0];

  return (
    <>
      <div className={`instant-dock${collapsed ? " instant-dock--collapsed" : ""}${isDarkMode ? "" : " instant-dock--light"}`} aria-label="Momentos">
        <button
          className="instant-dock__preview"
          onClick={() => { if (current) void openInstant(current); else if (!loadingInstants) void openCamera(); }}
          disabled={loadingInstants}
          type="button"
        >
          {current && <img {...protectedInstantImageProps} src={getImagePathBucketPacoca(current.media_url)} alt="Abrir momento" />}
          {!current && !loadingInstants && <Plus className="instant-dock__empty-plus" size={28} strokeWidth={2.4} />}
        </button>
      </div>

      {opened && (
        <div className="instant-viewer" role="dialog" aria-modal="true">
          <div
            className="instant-viewer__phone"
            onPointerDown={event => {
              if ((event.target as HTMLElement).closest('button,input')) return;
              pointerStartRef.current = { x: event.clientX, y: event.clientY };
            }}
            onPointerUp={event => {
              const start = pointerStartRef.current;
              pointerStartRef.current = null;
              if (!start || (event.target as HTMLElement).closest('button,input')) return;
              if (shouldAdvanceInstant(start, { x: event.clientX, y: event.clientY })) advanceInstant();
            }}
          >
            <header className="instant-viewer__header">
              <button onClick={closeViewer} type="button" aria-label="Fechar"><X size={30} /></button>
              <div>
                <button type="button" aria-label="Meus momentos" onClick={() => void openLibrary()}><Grid2X2 size={23} /></button>
                <button type="button" aria-label="Câmera" onClick={() => { closeViewer(); void openCamera(); }}><Camera size={24} /></button>
              </div>
            </header>
            <div className="instant-stack" key={opened.id}>
              <img {...protectedInstantImageProps} className="instant-stack__back instant-stack__back--one" src={getImagePathBucketPacoca(opened.media_url)} alt="" />
              <img {...protectedInstantImageProps} className="instant-stack__back instant-stack__back--two" src={getImagePathBucketPacoca(opened.media_url)} alt="" />
              <img {...protectedInstantImageProps} className="instant-stack__front" src={getImagePathBucketPacoca(opened.media_url)} alt="Momento" />
            </div>
            <div className="instant-owner">
              <img {...protectedInstantImageProps} src={getImagePathBucketPacoca(opened.user.img_account)} alt="" />
              <strong>{opened.user.user_name || opened.user.name}</strong>
              <span>{formatAge(opened.created_at)}</span>
              {!!opened.user.verified_profile && <i />}
            </div>
            <div className="instant-reactions">
              {REACTIONS.map(emoji => (
                <button key={emoji} type="button" className={opened.user_reaction === emoji ? "is-selected" : ""} onClick={() => void react(emoji)}>{emoji}</button>
              ))}
            </div>
            <div className="instant-emoji-burst" aria-hidden="true">
              {emojiParticles.map(particle => (
                <span
                  key={particle.id}
                  style={{
                    left: `${particle.left}%`,
                    fontSize: particle.size,
                    animationDelay: `${particle.delay}s`,
                    ["--emoji-drift" as any]: `${particle.drift}px`,
                  }}
                >{particle.emoji}</span>
              ))}
            </div>
            <div className="instant-reply">
              <input value={reply} onChange={event => setReply(event.target.value)} onKeyDown={event => { if (event.key === "Enter") void sendReply(); }} placeholder={`Responder a ${opened.user.user_name || opened.user.name}...`} />
              {reply.trim() && <button type="button" onClick={() => void sendReply()} aria-label="Enviar"><Send size={20} /></button>}
            </div>
          </div>
        </div>
      )}

      {showLibrary && (
        <section className="instant-library">
                <header>
                  <div>
                    <strong>Meus momentos</strong>
                    <span>Disponíveis por 24 horas</span>
                  </div>
                  <button type="button" onClick={() => setShowLibrary(false)} aria-label="Fechar"><X size={25} /></button>
                </header>
                {loadingLibrary ? (
                  <div className="instant-library__loading" />
                ) : myInstants.length ? (
                  <div className="instant-library__grid">
                    {myInstants.map(instant => (
                      <article key={instant.id}>
                        <img {...protectedInstantImageProps} src={getImagePathBucketPacoca(instant.media_url)} alt="" />
                        <span>{formatRemaining(instant.expires_at)}</span>
                        <button type="button" onClick={() => void deleteInstant(instant.id)} aria-label="Apagar momento"><Trash2 size={18} /></button>
                      </article>
                    ))}
                  </div>
                ) : (
                  <div className="instant-library__empty">
                    <Grid2X2 size={38} />
                    <p>Você ainda não publicou nenhum momento.</p>
                    <button type="button" onClick={() => { setShowLibrary(false); if (opened) { closeViewer(); void openCamera(); } }}>Criar momento</button>
                  </div>
          )}
        </section>
      )}


      {cameraOpen && (
        <div className="instant-camera" role="dialog" aria-modal="true">
          <div className="instant-camera__body">
            <div className="instant-camera__menu">
              <button onClick={closeCamera} type="button" aria-label="Fechar"><X size={28} /></button>
              <button onClick={() => void openLibrary()} type="button" aria-label="Meus momentos"><Grid2X2 size={24} /></button>
            </div>
            {cameraError ? <div className="instant-camera__error"><Camera size={36} /><p>{cameraError}</p><button onClick={() => void openCamera()} type="button">Tentar novamente</button></div> : <video ref={videoRef} playsInline muted />}
            {!cameraError && <button className="instant-camera__shutter" onClick={capture} disabled={uploading} type="button"><span /></button>}
            <p>Somente câmera · disponível por 24 horas</p>
          </div>
        </div>
      )}
    </>
  );
}

function formatRemaining(expiresAt: string): string {
  const minutes = Math.max(0, Math.ceil((new Date(expiresAt).getTime() - Date.now()) / 60000));
  if (minutes < 60) return `${minutes} min restantes`;
  return `${Math.ceil(minutes / 60)} h restantes`;
}

function formatAge(createdAt: string): string {
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(createdAt).getTime()) / 60000));
  if (minutes < 1) return "agora";
  if (minutes < 60) return `${minutes} min`;
  return `${Math.floor(minutes / 60)} h`;
}
